import { Inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { PaginatedRepository } from '../pagination/paginated.repository';
import { Alert } from '../../interfaces/alerts/alert';
import { AlertForCreation } from '../../interfaces/alerts/alert-for-creation';
import { AlertForUpdate } from '../../interfaces/alerts/alert-for-update';
import { AlertNoteForCreation } from '../../interfaces/alert-notes/alert-note-for-creation';
import { AlertNote } from '../../interfaces/alert-notes/alert-note';
import { PatchDocument } from '../../interfaces/patch-document';
import { TrackedAlertEntity } from '../../interfaces/alert-entities/tracked-alert-entity';
import { eAlertStatus } from '../../enums';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AlertsRepositoryServiceService extends PaginatedRepository<Alert> {
  protected override _baseLink: string;

  constructor(
    http: HttpClient,
    messageService: ToastrService,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
    super(http, messageService);

    this._baseLinkTemplate = `${config.rootUrl}/Corporations/[0]/Alerts`;
    this._baseLink = `${config.rootUrl}/Alerts`;
  }

  private generateHeaders = () => {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }
  }

  private getURL = (corporationID: string, alertID?: string) => {
    this.populateBaseLink([corporationID]);

    if (alertID) {
      return `${this._baseLink}/${alertID}`;
    }
    return this._baseLink;
  }

  public getAlerts = (corporationID: string, params?: HttpParams) => {
    return this.http.get<Alert[]>(this.getURL(corporationID), { params });
  }

  // Drives the Alerts sidebar badge. pageSize of 1 because only the x-pagination-resourcecount header is used.
  public getNewAlertCount = (corporationID: string): Observable<number> => {
    const params = new HttpParams()
      .set('AlertStatuses', eAlertStatus.New)
      .set('pageSize', '1');

    return this.http.get<Alert[]>(this.getURL(corporationID), { observe: 'response', params }).pipe(
      map(response => this.parseResourceCountHeader(response.headers))
    );
  }

  public getAlertAdmin = (alertID: string) => {
    return this.http.get<Alert>(`${this._baseLink}/${alertID}`);
  }

  public getAlert = (corporationID: string, alertID: string) => {
    return this.http.get<Alert>(this.getURL(corporationID, alertID));
  }

  public createAlert = (corporationID: string, newAlert: AlertForCreation) => {
    return this.http.post<Alert>(
      this.getURL(corporationID),
      newAlert,
      this.generateHeaders());
  }

  public updateAlert = (corporationID: string, alertID: string, updatedAlert: AlertForUpdate) => {
    return this.http.put<Alert>(
      this.getURL(corporationID, alertID),
      updatedAlert,
      this.generateHeaders());
  }

  public patchAlert = (corporationID: string, alertID: string, patch: PatchDocument[]) => {
    return this.http.patch<Alert>(
      this.getURL(corporationID, alertID),
      patch,
      this.generateHeaders());
  }

  public deleteAlert = (corporationID: string, alertID: string) => {
    return this.http.delete(this.getURL(corporationID, alertID));
  }

  public createAlertNote = (corporationsID: string, alertID: string, newNote: AlertNoteForCreation) => {
    return this.http.post<AlertNote>(
      `${this.getURL(corporationsID, alertID)}/Notes`,
      newNote,
      this.generateHeaders());
  }

  // The server sets the end date from its own clock, so the client never sends a time.
  // alertID is the alert it was done from; the API adds an activity entry to it.
  public stopTracking = (corporationID: string, trackedEntityID: string, alertID?: string) => {
    return this.http.post<TrackedAlertEntity>(`${this.getURL(corporationID)}/TrackedEntities/${trackedEntityID}/StopTracking`, null,
      { params: alertID ? new HttpParams().set('alertID', alertID) : undefined });
  }

  // trackingEndDate is when tracking ends again, sent as written (no time zone); leave it out to track with no end date
  public resumeTracking = (corporationID: string, trackedEntityID: string, alertID?: string, trackingEndDate?: string) => {
    let params = new HttpParams();
    if (alertID) {
      params = params.set('alertID', alertID);
    }
    if (trackingEndDate) {
      params = params.set('trackingEndDate', trackingEndDate);
    }

    return this.http.post<TrackedAlertEntity>(`${this.getURL(corporationID)}/TrackedEntities/${trackedEntityID}/ResumeTracking`, null, { params });
  }

  // Moves when tracking ends; same date format as resumeTracking, and leave trackingEndDate out for no end date
  public changeTrackingEndDate = (corporationID: string, trackedEntityID: string, trackingEndDate?: string) => {
    const params = trackingEndDate ? new HttpParams().set('trackingEndDate', trackingEndDate) : undefined;

    return this.http.post<TrackedAlertEntity>(`${this.getURL(corporationID)}/TrackedEntities/${trackedEntityID}/ChangeTrackingEndDate`, null, { params });
  }

  public deleteAlertNote = (corporationsID: string, alertID: string, alertNoteID: string) => {
    return this.http.delete(`${this.getURL(corporationsID, alertID)}/Notes/${alertNoteID}`);
  }
}